import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  signal,
  computed,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { GalleriaModule } from 'primeng/galleria';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

export interface GalleriaImage {
  id?: string;
  src: string;
  alt?: string;
  title?: string;
  description?: string;
  thumbnailSrc?: string;
  fileSize?: number; // Added for file size display
  isVideo?: boolean;
  videoUrl?: string;
  videoType?: 'SELF_HOSTED' | 'EXTERNAL_LINK';
  videoExternalPlatform?: 'YOUTUBE' | 'VIMEO';
  captionSrc?: string;
  captionLang?: string;
  captionLabel?: string;
}

export interface GalleriaConfig {
  showThumbnails?: boolean;
  showIndicators?: boolean;
  showItemNavigators?: boolean;
  showThumbnailNavigators?: boolean;
  showDownloadButton?: boolean;
  autoPlay?: boolean;
  circular?: boolean;
  numVisible?: number;
  responsiveOptions?: any[];
  transitionInterval?: number;
}

@Component({
  selector: 'app-galleria',
  standalone: true,
  imports: [CommonModule, GalleriaModule, ButtonModule, TranslateModule],
  templateUrl: './galleria.component.html',
  styleUrls: ['./galleria.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleriaComponent implements OnDestroy {
  @Input() images: GalleriaImage[] = [];
  @Input() config: GalleriaConfig = {};
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;
  @Input() emptyMessage: string = 'common.galleria.no_images';
  @Input() showEmptyState: boolean = true;

  // Debug counters
  private _hasImagesCallCount = 0;
  private _defaultConfigCallCount = 0;

  @Output() imageSelect = new EventEmitter<GalleriaImage>();
  @Output() imageChange = new EventEmitter<GalleriaImage>();
  @Output() fullscreenToggle = new EventEmitter<boolean>();
  @Output() downloadImage = new EventEmitter<GalleriaImage>();

  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  // Reactive state
  activeIndex = signal(0);
  isFullscreen = signal(false);
  isVisible = signal(true); // Set to true by default for inline display
  // Indexes of video items that failed to load (network/404/permission errors)
  brokenVideoIndexes = signal<Set<number>>(new Set());

  // Computed properties
  hasImages = computed(() => {
    this._hasImagesCallCount++;
    const result = this.images.length > 0;
    return result;
  });

  // Static config to prevent re-computations
  private static readonly STATIC_CONFIG: GalleriaConfig = {
    showThumbnails: true,
    showThumbnailNavigators: true, // Show thumbnail navigation buttons
    autoPlay: false,
    circular: false,
    numVisible: 6,
    responsiveOptions: [
      {
        breakpoint: '1024px',
        numVisible: 6,
      },
      {
        breakpoint: '768px',
        numVisible: 4,
      },
      {
        breakpoint: '560px',
        numVisible: 3,
      },
    ],
  };

  defaultConfig = computed((): GalleriaConfig => {
    this._defaultConfigCallCount++;
    return { ...GalleriaComponent.STATIC_CONFIG, ...this.config };
  });

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onImageSelect(event: any) {
    if (event && typeof event.index === 'number') {
      const selectedImage = this.images[event.index];
      if (selectedImage) {
        this.activeIndex.set(event.index);
        this.imageSelect.emit(selectedImage);
      }
    }
  }

  onImageChange(event: any) {
    if (event && typeof event.index === 'number') {
      const currentImage = this.images[event.index];
      if (currentImage) {
        this.activeIndex.set(event.index);
        this.imageChange.emit(currentImage);
      }
    }
  }

  onThumbnailClick(index: number) {
    if (
      index >= 0 &&
      index < this.images.length &&
      index !== this.activeIndex()
    ) {
      const selectedImage = this.images[index];
      this.activeIndex.set(index);
      this.imageSelect.emit(selectedImage);
      this.imageChange.emit(selectedImage);
    }
  }

  // Removed custom thumbnail click handler - using PrimeNG's built-in functionality

  onFullscreenToggle(event: any) {
    this.isFullscreen.set(event.isFullscreen);
    this.fullscreenToggle.emit(event.isFullscreen);
  }

  onDownloadImage(image: GalleriaImage) {
    this.downloadImage.emit(image);
  }

  // TrackBy function to optimize thumbnail rendering
  trackByImageId(index: number, item: GalleriaImage): string {
    return item.id || item.src || index.toString();
  }

  onShow() {
    this.isVisible.set(true);
  }

  onHide() {
    // Don't hide for inline display - keep visible
    // this.isVisible.set(false);
  }

  // Method to manually show galleria
  showGalleria() {
    this.isVisible.set(true);
  }

  // Method to manually change active index (for testing)
  setActiveIndex(index: number) {
    if (index >= 0 && index < this.images.length) {
      this.activeIndex.set(index);
      const selectedImage = this.images[index];
      this.imageSelect.emit(selectedImage);
      this.imageChange.emit(selectedImage);
    }
  }

  // Method to get current active index
  getCurrentIndex(): number {
    return this.activeIndex();
  }

  // Utility methods
  buildAltText(image: GalleriaImage): string {
    const altText =
      image.alt ||
      image.title ||
      `Gallery item ${this.images.indexOf(image) + 1}`;
    return (
      altText
        .replaceAll(/\bimage\b/gi, '')
        .replaceAll(/\s+/g, ' ')
        .trim() || `Gallery item ${this.images.indexOf(image) + 1}`
    );
  }

  getImageTitle(image: GalleriaImage): string {
    return image.title || image.description || '';
  }

  getImageDescription(image: GalleriaImage): string {
    return image.description || '';
  }

  // Navigation methods
  next() {
    const currentIndex = this.activeIndex();
    const nextIndex = this.defaultConfig().circular
      ? (currentIndex + 1) % this.images.length
      : Math.min(currentIndex + 1, this.images.length - 1);
    this.activeIndex.set(nextIndex);
  }

  prev() {
    const currentIndex = this.activeIndex();
    const prevIndex = this.defaultConfig().circular
      ? (currentIndex - 1 + this.images.length) % this.images.length
      : Math.max(currentIndex - 1, 0);
    this.activeIndex.set(prevIndex);
  }

  goToSlide(index: number) {
    if (index >= 0 && index < this.images.length) {
      this.activeIndex.set(index);
    }
  }

  // Image loading and error handling
  onImageError(event: any, image: GalleriaImage) {
    console.warn('Failed to load image:', image.src);
    // You can emit an error event or show a fallback image
  }

  onImageLoad(event: any, image: GalleriaImage) {
    // Image loaded successfully
  }

  // Video loading and error handling (network/404/permission errors -
  // codec-unsupported is already covered by the <video> element's own fallback content)
  onVideoError(_event: any, index: number) {
    console.warn('Failed to load video at index:', index);
    const updated = new Set(this.brokenVideoIndexes());
    updated.add(index);
    this.brokenVideoIndexes.set(updated);
  }

  isVideoBroken(index: number): boolean {
    return this.brokenVideoIndexes().has(index);
  }

  // Keyboard navigation
  onKeyDown(event: KeyboardEvent) {
    if (this.disabled) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.prev();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.next();
        break;
      case 'Escape':
        if (this.isFullscreen()) {
          event.preventDefault();
          this.isFullscreen.set(false);
        }
        break;
    }
  }

  // Accessibility
  getAriaLabel(image: GalleriaImage): string {
    return `Gallery item ${this.images.indexOf(image) + 1} of ${
      this.images.length
    }: ${this.buildAltText(image)}`;
  }

  getThumbnailAriaLabel(image: GalleriaImage): string {
    return `Thumbnail for ${this.buildAltText(image)}`;
  }
}
